import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { type ModalComponent, type ModalComponentState } from '@lib/entities/Modal';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';

/**
 * Redux twin of lib/state/Modal.ts (JSAD-7433). Pure: the `document.body.style.overflow`
 * writes the Zustand actions made on web (Modal.ts:49-59) belong to `modalSaga`, which reacts
 * to `openModal` and handles `closeModalRequest` (read `defaultBodyOverflow`, write the DOM,
 * then `put(closeModal())` — the read must precede the reset). Not reset on `auth/loggedOut`.
 */
export type Overflow = 'unset' | 'hidden';

export interface ModalState {
    open: boolean;
    state?: ModalComponentState<ModalComponent>;
    component?: ModalComponent;
    defaultBodyOverflow: Overflow;
}

const initialState: ModalState = {
    open: false,
    state: undefined,
    component: undefined,
    defaultBodyOverflow: 'unset'
};

/** Saga-only request: the reducer is a no-op; the saga settles `meta.deferred`. */
const request = <P, T = void>() => ({
    reducer: (_state: unknown, _action: DeferredAction<P, T>) => {},
    prepare: withDeferred<P, T>()
});

const modalSlice = createSlice({
    name: 'modal',
    initialState,
    reducers: {
        setDefaultBodyOverflow: (state, action: PayloadAction<Overflow>) => {
            state.defaultBodyOverflow = action.payload;
        },
        openModal: (state, action: PayloadAction<ModalComponent>) => {
            state.open = true;
            state.component = action.payload;
        },
        /** `Modal.ts:61` — back to the default state (including `defaultBodyOverflow`). */
        closeModal: () => initialState,
        changeState: (state, action: PayloadAction<ModalComponentState<ModalComponent>>) => {
            state.state = action.payload;
        },

        // ── Saga-only request: DOM overflow reset, then `closeModal` ───────────────────────
        closeModalRequest: request<void>()
    }
});

export const { setDefaultBodyOverflow, openModal, closeModal, changeState, closeModalRequest } =
    modalSlice.actions;

export const selectModal = (state: { modal: ModalState }) => state.modal;
export const selectModalOpen = (state: { modal: ModalState }) => state.modal.open;
export const selectModalComponent = (state: { modal: ModalState }) => state.modal.component;

export default modalSlice.reducer;
